/**
 * GuidedUploadView — Focused Source Upload Stage
 *
 * Shown after AI-assisted setup succeeds. The user uploads their COBOL source
 * archive here. On upload completion, the ingest result is handed to StandardView
 * which kicks off the full analysis pipeline automatically.
 *
 * This component reuses:
 *   - FileUploadDropzone (existing drag-drop component)
 *   - ingestExecutor (existing upload + polling logic)
 *   - createExecutionContext (existing context builder)
 *
 * It does NOT create parallel persistence or bypass any backend logic.
 */

import { useState, useRef, useCallback } from 'react'
import {
  ArrowLeft, Upload, CheckCircle2, Loader2, AlertTriangle,
  ChevronRight, FileCode, Sparkles, FolderOpen
} from 'lucide-react'
import FileUploadDropzone from '../designer/FileUploadDropzone'
import { execute as executeIngest } from '../../services/nodeExecutors/ingestExecutor'
import { createExecutionContext } from '../../services/workflowExecutor'
import { pollJobUntilComplete } from '../../utils/jobPoller'
import { getAPIBaseUrl } from '../../environment'
import './GuidedUploadView.css'

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / 1048576).toFixed(1) + ' MB'
}

// ── Status steps for the breadcrumb ─────────────────────────────────────────

const JOURNEY_STEPS = [
  { id: 'create', label: 'Setup', icon: Sparkles },
  { id: 'upload', label: 'Upload Source', icon: Upload },
  { id: 'analyze', label: 'Analyze', icon: FileCode },
]

// ── Main Component ──────────────────────────────────────────────────────────

function GuidedUploadView({ project, onBack, onUploadComplete, onBackToProjects }) {
  const [stagedFiles, setStagedFiles] = useState([])
  // waiting | selected | uploading | analyzing | complete | error
  const [uploadState, setUploadState] = useState('waiting')
  const [progress, setProgress] = useState({ pct: 0, msg: '' })
  const [error, setError] = useState(null)
  const [ingestResult, setIngestResult] = useState(null)
  const abortRef = useRef(null)

  const staged = stagedFiles[0] || null

  // Build execution context matching what StandardView uses
  const getExecCtx = useCallback(() => {
    let userId = ''
    try {
      userId = JSON.parse(localStorage.getItem('modernizeit_user'))?.user_id || ''
    } catch (_) {}

    return createExecutionContext(
      { account_id: project?.account || '' },
      { app_name: project?.name || '' },
      getAPIBaseUrl(),
      userId,
    )
  }, [project])

  // Handle file staging from dropzone
  const handleFileStaged = (files) => {
    setStagedFiles(files)
    setUploadState(files.length > 0 ? 'selected' : 'waiting')
    setError(null)
  }

  // Handle upload → ingest → auto-run code analysis → transition to Guided Analyze
  const handleUpload = async () => {
    if (!staged || uploadState === 'uploading' || uploadState === 'analyzing') return

    console.log('[GuidedUpload] Starting upload:', staged.name)
    setUploadState('uploading')
    setError(null)
    setProgress({ pct: 0, msg: 'Preparing upload...' })

    const controller = new AbortController()
    abortRef.current = controller

    try {
      // ── Phase 1: Ingest (upload + extract + catalog) ────────────────────────
      const ctx = getExecCtx()
      const config = {
        stagedFiles,
        sourceLanguage: 'cobol',
        targetLanguage: 'java',
        generateTypeMappings: true,
      }

      const ingestRes = await executeIngest(
        config,
        ctx,
        (pct, msg) => {
          // Map ingest progress 0-100 → display 0-45
          setProgress({ pct: Math.round(pct * 0.45), msg })
        },
        controller.signal,
      )

      const ingestJobId = ingestRes.jobs?.ingest_job_id
      console.log('[GuidedUpload] Ingest complete:', {
        source_hash: ingestRes.source_hash,
        ingest_job_id: ingestJobId,
        file_count: ingestRes.file_count,
      })

      setProgress({ pct: 48, msg: 'Files cataloged — starting COBOL analysis…' })

      // ── Phase 2: Auto-start Code Analysis ───────────────────────────────────
      setUploadState('analyzing')

      const API_BASE = getAPIBaseUrl()
      const token    = localStorage.getItem('modernizeit_token') || ''
      const fetchHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }

      let userId = ''
      try {
        userId = JSON.parse(localStorage.getItem('modernizeit_user') || '{}')?.user_id || ''
      } catch (_) {}

      console.log('[GuidedUpload] Starting code analysis for ingest job:', ingestJobId)

      const caStartRes = await fetch(`${API_BASE}/codeanalysis`, {
        method: 'POST',
        headers: fetchHeaders,
        signal: controller.signal,
        body: JSON.stringify({
          scout_account_id: project?.account || '',
          application_name: project?.name   || '',
          user_id:          userId,
          ingest_job_id:    ingestJobId,
          generate_java:    true,    // generate Java procedure code so Analyze view has it
          generate_graphs:  true,    // generate .dot call-graph files for Analyze view
        }),
      })

      if (!caStartRes.ok) {
        const errBody = await caStartRes.text()
        throw new Error(`Code Analysis failed to start (HTTP ${caStartRes.status}): ${errBody}`)
      }

      const caStartData = await caStartRes.json()
      const caJobId     = caStartData.job_id

      console.log('[GuidedUpload] Code analysis job queued:', caJobId)
      setProgress({ pct: 52, msg: 'Code analysis queued — waiting for worker…' })

      // ── Phase 3: Poll code analysis until complete ───────────────────────────
      await pollJobUntilComplete(caJobId, API_BASE, {
        maxWaitSeconds: 600,
        pollInterval:   3000,
        signal:         controller.signal,
        onProgress: (pct, msg) => {
          // Map CA poll progress 0-100 → display 52-95
          setProgress({
            pct: 52 + Math.round(pct * 0.43),
            msg: msg || 'Analyzing COBOL source…',
          })
        },
      })

      console.log('[GuidedUpload] Code analysis complete:', caJobId)

      // ── Phase 3b: Fire Data Analysis in background (fire and queue) ──────────
      // DA consumes the CA manifest and produces erd.json, jpa_entities.json,
      // schema_ddl.json, data_lineage.json.  It runs silently — Build Services
      // auto-discovers the SUCCEEDED DA job later.  Non-fatal if it fails to queue.
      let daJobId = null
      try {
        setProgress({ pct: 96, msg: 'Queuing Data Analysis…' })
        const daStartRes = await fetch(`${API_BASE}/dataanalysis`, {
          method: 'POST',
          headers: fetchHeaders,
          signal: controller.signal,
          body: JSON.stringify({
            scout_account_id: project?.account || '',
            application_name: project?.name   || '',
            user_id:          userId,
          }),
        })
        if (daStartRes.ok) {
          const daData = await daStartRes.json()
          daJobId = daData.job_id || null
          console.log('[GuidedUpload] Data Analysis queued:', daJobId)
        } else {
          console.warn('[GuidedUpload] Data Analysis failed to queue (non-fatal):', daStartRes.status)
        }
      } catch (daErr) {
        // Non-fatal — user still proceeds to Analyze, DA artifacts generated async
        console.warn('[GuidedUpload] Data Analysis queue error (non-fatal):', daErr.message)
      }

      // ── Phase 4: Fire Code Refactor in background (no poll — fire and queue) ──
      // CR runs silently while the user is on Analyze. GuidedAnalyzeView polls
      // its status and shows "Download Refactored Java" once the worker finishes.
      let crJobId = null
      try {
        setProgress({ pct: 97, msg: 'Queuing Code Refactor…' })
        const crStartRes = await fetch(`${API_BASE}/coderefactor`, {
          method: 'POST',
          headers: fetchHeaders,
          signal: controller.signal,
          body: JSON.stringify({
            scout_account_id: project?.account || '',
            application_name: project?.name   || '',
            user_id:          userId,
            mode:             'analyze',
          }),
        })
        if (crStartRes.ok) {
          const crData = await crStartRes.json()
          crJobId = crData.job_id || null
          console.log('[GuidedUpload] Code Refactor queued:', crJobId)
        } else {
          console.warn('[GuidedUpload] Code Refactor failed to queue (non-fatal):', crStartRes.status)
        }
      } catch (crErr) {
        // Non-fatal — user still proceeds to Analyze, button just won't appear
        console.warn('[GuidedUpload] Code Refactor queue error (non-fatal):', crErr.message)
      }

      // ── Phase 5: Build enriched result and transition ────────────────────────
      // Pass all job IDs so GuidedAnalyzeView can load real data without
      // a separate workflow-runs roundtrip.
      const enrichedResult = {
        ...ingestRes,
        jobs: {
          ...ingestRes.jobs,
          code_analysis_job_id:  caJobId,
          ...(daJobId ? { data_analysis_job_id: daJobId } : {}),
          ...(crJobId ? { code_refactor_job_id: crJobId } : {}),
        },
      }

      setIngestResult(enrichedResult)
      setUploadState('complete')
      setProgress({ pct: 100, msg: 'Analysis complete!' })

      console.log('[GuidedUpload] Ready for Guided Analyze:', {
        ingest_job_id:        ingestJobId,
        code_analysis_job_id: caJobId,
        data_analysis_job_id: daJobId,
        file_count:           enrichedResult.file_count,
      })

      // Brief pause so user sees the success state, then transition
      setTimeout(() => {
        onUploadComplete(enrichedResult)
      }, 1000)

    } catch (err) {
      if (err.name === 'AbortError' || err.message?.includes('cancelled')) {
        console.log('[GuidedUpload] Cancelled by user')
        return
      }
      console.error('[GuidedUpload] Upload/analysis failed:', err)
      setUploadState('error')
      setError(err.message || 'Upload or analysis failed')
      setProgress({ pct: 0, msg: '' })
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="guided-upload-view">
      {/* Top Bar */}
      <div className="gu-topbar">
        <button className="gu-back" onClick={onBack} title="Back to projects">
          <ArrowLeft size={18} />
        </button>
        <div className="gu-topbar-project">
          <FolderOpen size={16} />
          <span>{project?.name || 'New Project'}</span>
        </div>

        {/* Journey Breadcrumb */}
        <div className="gu-breadcrumb">
          {JOURNEY_STEPS.map((step, i) => {
            const Icon = step.icon
            const isCurrent = step.id === 'upload'
            const isDone = step.id === 'create'
            const isFuture = step.id === 'analyze'

            return (
              <div key={step.id} className="gu-breadcrumb-item-wrapper">
                {i > 0 && <ChevronRight size={14} className="gu-breadcrumb-separator" />}
                <div className={`gu-breadcrumb-item ${isCurrent ? 'current' : ''} ${isDone ? 'done' : ''} ${isFuture ? 'future' : ''}`}>
                  {isDone ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                  <span>{step.label}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="gu-content">
        <div className="gu-panel">
          {/* Header */}
          <div className="gu-header">
            <h1 className="gu-title">Upload Your Source Code</h1>
            <p className="gu-subtitle">
              Upload your COBOL source archive (.zip, .tar, .gz). Once uploaded,
              RecodeIT will automatically analyze your codebase.
            </p>
          </div>

          {/* Progress Bar — shown during both upload (ingest) and analysis phases */}
          {(uploadState === 'uploading' || uploadState === 'analyzing') && (
            <div className="gu-progress-section">
              <div className="gu-progress-phase-label">
                {uploadState === 'uploading' ? 'Step 1 of 2 — Uploading & Cataloging' : 'Step 2 of 2 — Analyzing COBOL Source'}
              </div>
              <div className="gu-progress-track">
                <div
                  className="gu-progress-fill"
                  style={{ width: `${Math.max(progress.pct || 0, 4)}%` }}
                />
              </div>
              <div className="gu-progress-label">
                <Loader2 size={14} className="spin" />
                <span>{progress.msg || (uploadState === 'uploading' ? 'Uploading...' : 'Analyzing…')}</span>
                {progress.pct > 0 && (
                  <span className="gu-progress-pct">{Math.round(progress.pct)}%</span>
                )}
              </div>
            </div>
          )}

          {/* Success State — only shown once BOTH ingest AND code analysis are complete */}
          {uploadState === 'complete' && (
            <div className="gu-success">
              <CheckCircle2 size={24} />
              <div>
                <p className="gu-success-title">Analysis Complete!</p>
                <p className="gu-success-sub">
                  {ingestResult?.file_count
                    ? `${ingestResult.file_count} files extracted and analyzed.`
                    : 'Files extracted and analyzed.'
                  }
                  {' '}Loading results…
                </p>
              </div>
              <Loader2 size={16} className="spin" style={{ marginLeft: 'auto' }} />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="gu-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Dropzone (hidden while uploading, analyzing, or complete) */}
          {uploadState !== 'uploading' && uploadState !== 'analyzing' && uploadState !== 'complete' && (
            <>
              {/* Status Cards */}
              <div className="gu-status-row">
                <div className={`gu-status-card ${staged ? 'ready' : 'waiting'}`}>
                  <span className="gu-status-label">Status</span>
                  <span className="gu-status-value">
                    {staged ? 'Ready to Upload' : 'Waiting for File'}
                  </span>
                </div>
                {staged && (
                  <div className="gu-status-card ready">
                    <span className="gu-status-label">Selected File</span>
                    <span className="gu-status-value">
                      {staged.name}
                      <span className="gu-status-size">{formatBytes(staged.size)}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Dropzone */}
              <div className="gu-dropzone-area">
                <FileUploadDropzone
                  config={{ accept: '.zip,.tar,.gz' }}
                  onFileStaged={handleFileStaged}
                />
              </div>

              {/* Action Button */}
              <div className="gu-actions">
                <button
                  className={`gu-upload-btn ${!staged ? 'disabled' : ''}`}
                  onClick={handleUpload}
                  disabled={!staged || uploadState === 'uploading' || uploadState === 'analyzing'}
                >
                  <Upload size={16} />
                  Upload & Start Analysis
                </button>
              </div>
            </>
          )}

          {/* What Happens Next — shown only in idle states, not during active processing */}
          {uploadState !== 'uploading' && uploadState !== 'analyzing' && uploadState !== 'complete' && (
            <div className="gu-info-section">
              <h3 className="gu-info-title">What happens next?</h3>
              <div className="gu-info-steps">
                <div className="gu-info-step">
                  <span className="gu-info-num">1</span>
                  <div>
                    <strong>Upload & Extract</strong>
                    <p>Your archive is uploaded and all source files are extracted and cataloged.</p>
                  </div>
                </div>
                <div className="gu-info-step">
                  <span className="gu-info-num">2</span>
                  <div>
                    <strong>Automatic Analysis</strong>
                    <p>Code Analysis runs automatically — extracting structure, dependencies, and generating initial Java.</p>
                  </div>
                </div>
                <div className="gu-info-step">
                  <span className="gu-info-num">3</span>
                  <div>
                    <strong>Full Pipeline</strong>
                    <p>All 12 transformation stages run in sequence — AI Naming, Refactor, Service Generation, and more.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GuidedUploadView
